/**
 * IssueCard
 *
 * Collapsible row for a single Coverage Audit issue.
 *
 * - Header: severity badge, family code, short rationale, confidence,
 *   disposition hint, and current status.
 * - Expanded body: evidence bundle and reason codes.
 * - When the issue is reviewable (`open` or `needs_review`), exposes
 *   accept / reject / edit / ignore action buttons that open `FeedbackDialog`.
 *
 * State lives in `useCoverageAuditStore`. Auth is handled by `apiService`
 * interceptors — no token is passed through. `submitFeedback` from the store
 * already refreshes the encounter's issue list after the round-trip, so this
 * component does not need to call `loadEncounterIssues` manually.
 */
import React, { useState } from 'react';
import {
  useCoverageAuditStore,
  type EvidenceSpan,
  type IssueRow,
  type IssueReviewAction,
} from '../../stores/coverageAuditStore';
import { FeedbackDialog } from './FeedbackDialog';
import { getRepairProposal, type RepairProposal } from './repairProposals';
import type { ClinicalSummary } from '../../types/clinicalSummary';
import type { MedicalNote } from '../../types/medicalNote';

const SEVERITY_BADGE: Record<IssueRow['severity'], string> = {
  major: 'bg-red-100 text-red-700',
  moderate: 'bg-yellow-100 text-yellow-700',
  minor: 'bg-gray-100 text-gray-700',
};

const STATUS_BADGE: Record<IssueRow['status'], string> = {
  open: 'bg-blue-100 text-blue-700',
  needs_review: 'bg-purple-100 text-purple-700',
  auto_resolved: 'bg-green-100 text-green-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  dismissed: 'bg-gray-100 text-gray-600',
};

const ACTION_BUTTON_CLASS: Record<IssueReviewAction, string> = {
  accepted: 'bg-green-100 text-green-800 hover:bg-green-200',
  rejected: 'bg-red-100 text-red-800 hover:bg-red-200',
  edited: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
  ignored: 'bg-gray-200 text-gray-800 hover:bg-gray-300',
};

const ACTION_LABEL: Record<IssueReviewAction, string> = {
  accepted: 'Accept',
  rejected: 'Reject',
  edited: 'Edit',
  ignored: 'Ignore',
};

export interface IssueCardProps {
  issue: IssueRow;
  // Optional. When provided, evidence list items become buttons that
  // scroll-and-flash the corresponding transcript segment / section card on
  // the detail page. Decoupled so the IssueCard stays usable in places that
  // don't need anchoring (e.g. a future read-only digest view).
  onEvidenceClick?: (span: EvidenceSpan) => void;
  // Pass-through summary + note context so the repair-proposal generator
  // can build a section-content diff. When omitted, proposals that need
  // section content (set_section_content, clear_section) won't render
  // their `current` / `proposed` previews.
  summary?: ClinicalSummary | null;
  note?: MedicalNote | null;
}

export const IssueCard: React.FC<IssueCardProps> = ({
  issue,
  onEvidenceClick,
  summary,
  note,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [pendingAction, setPendingAction] = useState<IssueReviewAction | null>(
    null,
  );

  const submitFeedback = useCoverageAuditStore((s) => s.submitFeedback);

  const reviewable =
    issue.status === 'open' || issue.status === 'needs_review';

  // Repair proposal — derived from the issue's attributes + the encounter
  // context. v1 displays the proposal; v2 (here) wires the Apply button
  // to POST /v1/coverage-audit/issues/:id/repair which mutates the
  // medical note + records an audit trail in a single transaction.
  const repair = getRepairProposal({ issue, summary, note });
  const applyRepairFn = useCoverageAuditStore((s) => s.applyRepair);
  const applyRepairLoading = useCoverageAuditStore(
    (s) => !!s.applyRepairLoading[issue.id],
  );
  const applyRepairErrorMessage = useCoverageAuditStore(
    (s) => s.applyRepairError[issue.id] ?? null,
  );

  const handleApplyRepair = async () => {
    if (!repair?.autoApplicable) return;
    try {
      await applyRepairFn({
        issueId: issue.id,
        encounterId: issue.encounterId,
      });
      // The store refreshes the encounter's issue list — the now-
      // confirmed issue will drop out of the open rail on the next
      // render.
    } catch (_err) {
      // Error already in store.applyRepairError.
    }
  };

  const handleSubmit = async (resolutionReason: string) => {
    if (!pendingAction) return;
    // Store auto-refreshes encounter issues after the feedback round-trip.
    await submitFeedback({
      issueId: issue.id,
      encounterId: issue.encounterId,
      reviewAction: pendingAction,
      resolutionReason,
    });
  };

  const toggleExpanded = () => setExpanded((v) => !v);
  const onHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <div className="border border-gray-200 rounded-md p-3 mb-2 bg-white">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggleExpanded}
        onKeyDown={onHeaderKeyDown}
        className="flex flex-wrap items-center gap-2 cursor-pointer"
      >
        <span
          className={`text-xs font-medium px-2 py-1 rounded ${SEVERITY_BADGE[issue.severity]}`}
        >
          {issue.severity}
        </span>
        <span className="font-mono text-xs text-gray-500 truncate min-w-0">
          {issue.family}
        </span>
        <span className="text-xs text-gray-500 tabular-nums shrink-0">
          {(issue.confidence * 100).toFixed(0)}%
        </span>
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded shrink-0 ${STATUS_BADGE[issue.status]}`}
        >
          {issue.status}
        </span>
        {/* Phase 3 — detector trust badge. Shown only when at least one
            contributing detector has feedback in the lookback window.
            Color-codes confidence so the auditor can scan the rail and
            give more weight to historically reliable signals. Tooltip
            carries the full breakdown (decisions, accept rate). */}
        {issue.trust && issue.trust.totalDecisions > 0 && (
          <span
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${
              issue.trust.aggregateScore >= 0.7
                ? 'bg-emerald-100 text-emerald-700'
                : issue.trust.aggregateScore >= 0.4
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-700'
            }`}
            title={`Aggregate detector trust ${(issue.trust.aggregateScore * 100).toFixed(0)}% across ${issue.trust.detectorCount} detector${issue.trust.detectorCount > 1 ? 's' : ''}, ${issue.trust.totalDecisions} prior decisions in 30d. Min detector score ${(issue.trust.minScore * 100).toFixed(0)}%.`}
          >
            trust {(issue.trust.aggregateScore * 100).toFixed(0)}%
          </span>
        )}
        {/* Auto-fix badge in the collapsed header so the auditor can
            scan the rail and prioritise quick wins without expanding
            every card. The button itself sits inside the expanded
            repair block so the auditor sees the diff before committing. */}
        {repair?.autoApplicable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!expanded) setExpanded(true);
              void handleApplyRepair();
            }}
            disabled={applyRepairLoading}
            className="ml-auto text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 shrink-0"
            title="Apply this auto-fix. Patches the medical note + records audit trail."
          >
            {applyRepairLoading ? 'Applying…' : '⚡ Apply fix'}
          </button>
        )}
      </div>
      <p className="mt-1.5 text-sm text-gray-800 leading-snug break-words">
        {issue.rationaleShort}
      </p>
      {/* Compact preview of the proposal under rationale — visible
          even when the card is collapsed so the auditor sees what
          would happen without expanding. */}
      {!expanded && repair?.autoApplicable && (
        <p className="mt-0.5 text-xs text-emerald-700 leading-snug">
          ↳ {repair.headline}
        </p>
      )}
      <div className="mt-1 text-xs text-gray-500 italic">
        {issue.disposition}
      </div>

      {expanded && (
        <div className="mt-3 text-sm text-gray-800">
          <div className="font-semibold mb-1">Evidence</div>
          {issue.evidenceBundle.length === 0 ? (
            <div className="text-xs text-gray-500 italic mb-2">
              No evidence spans recorded.
            </div>
          ) : (
            <ul className="list-none pl-0 mb-3 space-y-1">
              {issue.evidenceBundle.map((e, i) => {
                const label = (
                  <>
                    <span className="text-xs text-gray-500">
                      [{e.source}
                      {e.section ? ` · ${e.section}` : ''}
                      {typeof e.segmentOrderStart === 'number'
                        ? ` · ${e.segmentOrderStart}${
                            typeof e.segmentOrderEnd === 'number' &&
                            e.segmentOrderEnd !== e.segmentOrderStart
                              ? `–${e.segmentOrderEnd}`
                              : ''
                          }`
                        : ''}
                      ]
                    </span>{' '}
                    <span className="italic">&ldquo;{e.text}&rdquo;</span>
                  </>
                );
                return onEvidenceClick ? (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onEvidenceClick(e);
                      }}
                      className="text-left w-full block px-2 py-1 rounded hover:bg-amber-50 hover:ring-1 hover:ring-amber-200 transition-colors cursor-pointer"
                      title="Jump to this evidence in the source column"
                    >
                      <span className="text-amber-600 mr-1">↗</span>
                      {label}
                    </button>
                  </li>
                ) : (
                  <li key={i} className="px-2 py-1">
                    {label}
                  </li>
                );
              })}
            </ul>
          )}

          {repair && (
            <RepairProposalBlock
              proposal={repair}
              severity={issue.severity}
              onApply={repair.autoApplicable ? handleApplyRepair : undefined}
              applying={applyRepairLoading}
              applyError={applyRepairErrorMessage}
            />
          )}

          {/* Plain-English summary of the policy decision. The raw codes
              are kept available for engineering debug behind a disclosure
              below — see learning-loop spec §5. */}
          {issue.summary && (
            <div className="text-sm text-gray-800 mb-3 leading-snug">
              {issue.summary}
            </div>
          )}

          <details className="mb-3">
            <summary className="cursor-pointer text-[11px] uppercase tracking-wide text-gray-500 font-semibold select-none">
              🔧 Detector reasoning
            </summary>
            <div className="text-xs text-gray-600 mt-1 font-mono">
              {issue.reasonCodes.length > 0
                ? issue.reasonCodes.join(', ')
                : '—'}
            </div>
          </details>

          {reviewable && (
            <div className="flex flex-wrap gap-2">
              {(
                ['accepted', 'rejected', 'edited', 'ignored'] as const
              ).map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setPendingAction(a)}
                  className={`text-xs font-medium px-3 py-1 rounded transition-colors ${ACTION_BUTTON_CLASS[a]}`}
                >
                  {ACTION_LABEL[a]}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <FeedbackDialog
        isOpen={pendingAction !== null}
        action={pendingAction}
        onSubmit={handleSubmit}
        onClose={() => setPendingAction(null)}
      />
    </div>
  );
};

export default IssueCard;

// ---------------------------------------------------------------------------
// RepairProposalBlock — renders the suggested fix for an issue.
// v1 is read-only ("Apply" button is disabled with a tooltip explaining
// that v2 will wire the actual write path). The block surfaces enough
// info for the auditor to mentally validate the proposal: the kind of
// edit, what's there now, what it would become, and the rationale.
// ---------------------------------------------------------------------------

const REPAIR_KIND_LABEL: Record<RepairProposal['kind'], string> = {
  replace_text: 'Replace text',
  set_section_content: 'Replace section',
  clear_section: 'Clear section',
  add_to_section: 'Add to section',
  manual: 'Manual review',
  unsupported: 'No template',
};

const REPAIR_KIND_CLS: Record<RepairProposal['kind'], string> = {
  replace_text: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  set_section_content: 'bg-blue-50 border-blue-200 text-blue-800',
  clear_section: 'bg-red-50 border-red-200 text-red-800',
  add_to_section: 'bg-purple-50 border-purple-200 text-purple-800',
  manual: 'bg-amber-50 border-amber-200 text-amber-800',
  unsupported: 'bg-gray-50 border-gray-200 text-gray-700',
};

// Severity-aware overlay for the manual kind. Auto-applicable kinds
// already carry a kind-specific color; the manual kind otherwise looks
// uniform across major/moderate/minor issues. Tinting the left border
// by severity lets the auditor scan the rail and prioritise without
// expanding every card. Only the border is tinted — the body keeps
// the kind's color so the kind label still reads cleanly.
const SEVERITY_BORDER: Record<IssueRow['severity'], string> = {
  major: 'border-l-4 border-l-red-400',
  moderate: 'border-l-4 border-l-amber-400',
  minor: 'border-l-4 border-l-yellow-300',
};

function RepairProposalBlock({
  proposal,
  severity,
  onApply,
  applying,
  applyError,
}: {
  proposal: RepairProposal;
  severity?: IssueRow['severity'];
  // Fires when the auditor confirms the proposal. Only auto-applicable
  // proposals receive a handler; manual / unsupported pass undefined.
  onApply?: () => void | Promise<void>;
  applying?: boolean;
  applyError?: string | null;
}) {
  const showDiff =
    proposal.current !== undefined || proposal.proposed !== undefined;
  // Apply severity tint only for manual kinds — auto-applicable kinds
  // already convey priority via their distinctive color.
  const severityCls =
    severity && proposal.kind === 'manual' ? SEVERITY_BORDER[severity] : '';
  return (
    <div
      className={`mb-3 border rounded p-2 ${REPAIR_KIND_CLS[proposal.kind]} ${severityCls}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-white/60">
            {REPAIR_KIND_LABEL[proposal.kind]}
          </span>
          {proposal.targetSection && (
            <span className="text-[10px] font-mono text-gray-600">
              note · {proposal.targetSection}
            </span>
          )}
        </div>
        {onApply ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void onApply();
            }}
            disabled={applying}
            className="text-[11px] px-2.5 py-1 rounded bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Apply this fix to the medical note. Records an audit entry + feedback row."
          >
            {applying ? 'Applying…' : 'Apply'}
          </button>
        ) : (
          <span
            className="text-[11px] px-2 py-0.5 rounded bg-white/60 text-gray-500"
            title="Manual review required — apply directly in the EHR after assessment."
          >
            Manual
          </span>
        )}
      </div>
      <div className="text-[13px] font-semibold leading-snug mb-1">
        {proposal.headline}
      </div>
      {proposal.rationale && (
        <div className="text-[11px] opacity-80 leading-snug mb-1.5">
          {proposal.rationale}
        </div>
      )}
      {applyError && (
        <div className="mb-1.5 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {applyError}
        </div>
      )}
      {showDiff && (
        <div className="grid grid-cols-2 gap-2 text-[12px]">
          <div className="border border-red-200 bg-red-50/40 rounded px-1.5 py-1">
            <div className="text-[9px] uppercase tracking-wide font-semibold text-red-600 mb-0.5">
              Current
            </div>
            <div className="text-red-900 whitespace-pre-wrap break-words">
              {proposal.current && proposal.current.length > 0 ? (
                proposal.current.length > 200 ? (
                  proposal.current.slice(0, 200) + '…'
                ) : (
                  proposal.current
                )
              ) : (
                <span className="italic opacity-60">(empty)</span>
              )}
            </div>
          </div>
          <div className="border border-emerald-200 bg-emerald-50/40 rounded px-1.5 py-1">
            <div className="text-[9px] uppercase tracking-wide font-semibold text-emerald-700 mb-0.5">
              Proposed
            </div>
            <div className="text-emerald-900 whitespace-pre-wrap break-words">
              {proposal.proposed && proposal.proposed.length > 0 ? (
                proposal.proposed.length > 200 ? (
                  proposal.proposed.slice(0, 200) + '…'
                ) : (
                  proposal.proposed
                )
              ) : (
                <span className="italic opacity-60">(empty)</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
